import { interpolate, Route, toAssignmentHref } from "@sol/routing";
import { EntityTypes, getResourceUuid, type INotificationResource, NotificationType } from "@sol/sdk";

import { FollowUpKind } from "@/entities/follow-up";
import {
    isBriefNotification,
    isFollowUpNotification,
    isGroupNotification,
    isSelfEvaluationNotification,
} from "@/entities/notification";

// A learner follows each assignment in their own workspace, which the notification links to by its topic or group space
const LEARNER_ASSIGNMENT_NOTIFICATIONS = new Set<NotificationType>([
    NotificationType.MESSAGE_TRAINER_TO_LEARNER,
    NotificationType.MESSAGE_TUTOR_TO_LEARNER,
    NotificationType.WORK_CORRECTED,
    NotificationType.GROUP_SPACE_TO_LEARNER,
    NotificationType.GROUP_WORK_TO_LEARNER,
    NotificationType.GROUP_CORRECTION_TO_LEARNER,
    NotificationType.GROUP_MESSAGE_LEARNER_TO_LEARNER,
    NotificationType.GROUP_MESSAGE_TRAINER_TO_LEARNER,
    NotificationType.GROUP_MESSAGE_TUTOR_TO_LEARNER,
]);

export type NotificationRedirectSource = Pick<INotificationResource, "type"> & {
    recipientClassroom?: INotificationResource["recipientClassroom"];
    linkEntity?: Partial<Pick<INotificationResource["linkEntity"], "@id" | "@type">>;
};

const getClassroomWorkspaceRedirect = (recipientClassroom: string | undefined, query: Record<string, string>) =>
    recipientClassroom
        ? interpolate(Route.CLASSROOM_WORKSPACES, { classroomId: getResourceUuid(recipientClassroom), ...query })
        : Route.HOME;

// Notifications whose linked entity was deleted have nothing to open
export const getNotificationRedirect = (notification: NotificationRedirectSource): string => {
    const { linkEntity, recipientClassroom, type } = notification;
    const linkEntityId = linkEntity?.["@id"];

    if (type === NotificationType.BRIEF_ASSIGNED) {
        // The notification links the brief rather than the learner assignment.
        return Route.MY_WORKSPACE;
    }

    if (LEARNER_ASSIGNMENT_NOTIFICATIONS.has(type)) {
        return linkEntityId ? toAssignmentHref(getResourceUuid(linkEntityId)) : Route.MY_WORKSPACE;
    }

    if (!linkEntityId) {
        return Route.HOME;
    }

    const linkEntityUuid = getResourceUuid(linkEntityId);

    if (isGroupNotification(type)) {
        return getClassroomWorkspaceRedirect(recipientClassroom, { tab: "group", groupSpace: linkEntityUuid });
    }

    if (isFollowUpNotification(type) || isSelfEvaluationNotification(type)) {
        return interpolate(Route.FOLLOW_UPS_DETAILS, {
            followUpId: linkEntityUuid,
            kind:
                linkEntity?.["@type"] === EntityTypes.SELF_EVALUATION
                    ? FollowUpKind.SELF_EVALUATION
                    : FollowUpKind.FOLLOW_UP,
        });
    }

    if (isBriefNotification(type)) {
        return interpolate(Route.BRIEFS_DETAILS, { id: linkEntityUuid });
    }

    return getClassroomWorkspaceRedirect(recipientClassroom, { topic: linkEntityUuid });
};
